import type JQueryStatic from 'jquery';

/**
 * Lazily loads jQuery, Select2, and the vendored PTLINKED plugin (JS + CSS), in the order
 * the PTLINKED plugin requires, exactly once per page session.
 *
 * This dynamic `import('./ptlinkedVendorBundle')` is what gets the ~70KB of vendor JS/CSS
 * code-split into its own chunk, only fetched the first time this is called rather than on
 * initial page load — jQuery and Select2 are loaded as real ESM imports inside that module.
 * The vendor plugin script itself is a different story: `ptlinkedVendorBundle`'s default
 * export is a `Promise<JQueryStatic>` that only resolves once a runtime `<script>` tag has
 * loaded it (see that file for why — a static `import` of it is broken, not just a style
 * choice). `.then((module) => module.default)` below transparently unwraps that inner
 * promise; nothing here needs to know the vendor script isn't a real ES module.
 *
 * Concurrent/repeated calls (e.g. React 19 StrictMode's double-invoked effects) share the
 * same in-flight promise rather than triggering a second load. On failure, this module's own
 * cache is cleared so a later call will attempt a fresh `import()` rather than replaying the
 * same rejection forever — note that doesn't guarantee the underlying network fetch itself
 * retries cleanly, since a module that throws during evaluation can be treated as permanently
 * failed by some import mechanisms; this only controls what *our* wrapper does.
 */
let loadPromise: Promise<JQueryStatic> | null = null;

export function loadPtlinkedAssets(): Promise<JQueryStatic> {
  if (!loadPromise) {
    loadPromise = import('./ptlinkedVendorBundle')
      .then((module) => module.default)
      .catch((error: unknown) => {
        loadPromise = null;
        throw error;
      });
  }
  return loadPromise;
}
